import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { BRAND } from "@/config/links";

const UPDATED = "22 July 2026";

const SECTIONS = [
  {
    h: "1. Who we are",
    body: (
      <>
        <p>
          {BRAND.company} (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) is an executive
          communication consultancy founded and operated by {BRAND.coach}, based in Brazil. This
          Privacy Policy explains how we collect, use and protect your personal data when you visit{" "}
          <a href="https://interactglobally.com.br/" className="underline decoration-terracotta/60 hover:text-terracotta">interactglobally.com.br</a>{" "}
          (the &ldquo;Website&rdquo;) or contact us in any way.
        </p>
        <p className="mt-3">
          This Policy is written to comply with the Brazilian General Data Protection Law
          (Lei n&ordm; 13.709/2018 — LGPD) and with international good practice.
        </p>
      </>
    ),
  },
  {
    h: "2. Data we collect",
    body: (
      <>
        <p>We collect only what is necessary to provide our services and reply to enquiries:</p>
        <ul className="list-disc pl-6 mt-3 space-y-2">
          <li><strong>Contact form data</strong> — full name, role, email address, country, message, and optionally company and phone number.</li>
          <li><strong>Diagnostic responses</strong> — answers you provide to our Presentation Readiness diagnostic (hosted by ScoreApp).</li>
          <li><strong>Booking data</strong> — name, email and preferred time when you book a consultation (via TidyCal).</li>
          <li><strong>Technical data</strong> — pages visited, referring URL, browser, device type, approximate location. Collected only when you consent to analytics.</li>
        </ul>
      </>
    ),
  },
  {
    h: "3. Legal basis for processing (LGPD Art. 7)",
    body: (
      <>
        <p>We rely on the following legal bases:</p>
        <ul className="list-disc pl-6 mt-3 space-y-2">
          <li><strong>Consent</strong> — for analytics cookies, marketing communications and contact-form submissions.</li>
          <li><strong>Contract performance</strong> — where you are a client and processing is necessary to deliver our services.</li>
          <li><strong>Legitimate interest</strong> — to protect the Website, prevent fraud, and improve our services in a proportionate way.</li>
        </ul>
      </>
    ),
  },
  {
    h: "4. How we use your data",
    body: (
      <ul className="list-disc pl-6 space-y-2">
        <li>To respond personally to your enquiries.</li>
        <li>To deliver coaching, training or partnership services you have engaged us for.</li>
        <li>To send the results of the Presentation Readiness diagnostic and, where you consent, related follow-ups.</li>
        <li>To improve the Website based on aggregated, anonymised usage patterns.</li>
      </ul>
    ),
  },
  {
    h: "5. Sharing with third parties",
    body: (
      <>
        <p>We use trusted third-party providers to operate the Website and our services:</p>
        <ul className="list-disc pl-6 mt-3 space-y-2">
          <li><strong>ActiveCampaign</strong> — CRM and email delivery (US-based).</li>
          <li><strong>TidyCal</strong> — booking calendar embed (US-based).</li>
          <li><strong>ScoreApp</strong> — diagnostic hosting (UK-based).</li>
          <li><strong>Google Analytics 4</strong> — anonymised traffic analytics (US-based).</li>
          <li><strong>PostHog</strong> — product analytics and session recording (EU-hosted).</li>
          <li><strong>Vimeo</strong> — testimonial video hosting (US-based).</li>
          <li><strong>Meta / LinkedIn</strong> — where you follow us or click a social link.</li>
        </ul>
        <p className="mt-3">
          These providers act as data processors under our instructions. International transfers use
          the safeguards permitted by LGPD (Art. 33) — contractual clauses, adequacy decisions or
          your explicit consent.
        </p>
      </>
    ),
  },
  {
    h: "6. Cookies",
    body: (
      <p>
        We use a cookie-consent banner on your first visit. You can accept all, reject all
        non-essential, or choose exactly which categories you allow (necessary / analytics /
        marketing). Necessary cookies are always active because the Website cannot function
        without them. Your choice is stored in your browser and you may change it any time.
      </p>
    ),
  },
  {
    h: "7. Data retention",
    body: (
      <ul className="list-disc pl-6 space-y-2">
        <li>Contact-form enquiries: up to 24 months after last contact, or until you request deletion.</li>
        <li>Client data: for the duration of the engagement plus 5 years for accounting and legal reasons.</li>
        <li>Analytics data: 14 months (GA4 default retention).</li>
      </ul>
    ),
  },
  {
    h: "8. Your rights (LGPD Art. 18)",
    body: (
      <>
        <p>As the data subject (<em>titular</em>), you have the right to:</p>
        <ul className="list-disc pl-6 mt-3 space-y-2">
          <li>Confirm we hold your data and access it.</li>
          <li>Correct incomplete, inaccurate or outdated data.</li>
          <li>Anonymise, block or delete unnecessary or excessive data.</li>
          <li>Data portability to another provider.</li>
          <li>Delete data processed based on your consent.</li>
          <li>Withdraw consent at any time.</li>
          <li>Object to processing that violates LGPD.</li>
          <li>Complain to the National Data Protection Authority (<em>ANPD</em>).</li>
        </ul>
        <p className="mt-3">
          To exercise any of these rights, email{" "}
          <a href="mailto:christos@interactglobally.com.br" className="underline decoration-terracotta/60 hover:text-terracotta">christos@interactglobally.com.br</a>{" "}
          with the subject &ldquo;LGPD request&rdquo;. We reply within 15 days.
        </p>
      </>
    ),
  },
  {
    h: "9. Security",
    body: (
      <p>
        We apply reasonable technical and organisational measures to protect your data —
        encrypted transport (HTTPS), least-privilege access, and periodic reviews of our
        third-party processors. No system is 100% secure; we notify affected data subjects and
        the ANPD in the event of a qualifying incident.
      </p>
    ),
  },
  {
    h: "10. Changes to this policy",
    body: (
      <p>
        We may update this Privacy Policy from time to time. The &ldquo;Last updated&rdquo; date
        at the top reflects the most recent revision. Material changes will be highlighted on the
        Website.
      </p>
    ),
  },
  {
    h: "11. Contact",
    body: (
      <p>
        Questions about this Privacy Policy or your personal data? Email{" "}
        <a href="mailto:christos@interactglobally.com.br" className="underline decoration-terracotta/60 hover:text-terracotta">
          christos@interactglobally.com.br
        </a>{" "}
        — we&rsquo;ll come back within one working day.
      </p>
    ),
  },
];

export default function Privacy() {
  return (
    <>
      <PageHero
        testId="privacy-hero"
        eyebrow="Privacy policy"
        headline={["How we handle", "your data."]}
        emphasis="Clear, respectful, LGPD-compliant."
        emphasisAccent="underline"
        intro={`Last updated ${UPDATED}. This policy explains what we collect, why we collect it, and the rights you have over your personal data.`}
      />

      <section className="relative z-10 pb-32">
        <div className="max-w-[820px] mx-auto px-5 md:px-10 space-y-10 font-body text-ink-soft leading-relaxed">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.h} delay={i * 0.03}>
              <div className="border-t border-ink/15 pt-8">
                <h2 className="font-serif text-2xl md:text-3xl text-ink mb-4">{s.h}</h2>
                <div className="space-y-3">{s.body}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}
